import { useQuery } from '@tanstack/react-query';
import { useProject } from '@/contexts/ProjectContext';
import { Code } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { getGBrainContext } from '@/lib/api';

/**
 * TechStackPanel
 *
 * Renders language/framework badges from getGBrainContext
 */
export function TechStackPanel(): React.ReactElement | null {
  const { selectedProjectId } = useProject();

  const { data: contextData } = useQuery({
    queryKey: ['g-brain-context', selectedProjectId],
    queryFn: () => {
      if (!selectedProjectId) throw new Error('No project selected');
      return getGBrainContext(selectedProjectId);
    },
    enabled: !!selectedProjectId,
    staleTime: 30_000,
  });

  const context = contextData?.context;

  if (!selectedProjectId) {
    return null;
  }

  const techStack = context?.techStack ?? [];

  return (
    <div className="space-y-2">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-text-tertiary flex items-center gap-1">
        <Code className="h-3.5 w-3.5" />
        Tech Stack
      </div>

      {techStack.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {techStack.map((t, i) => (
            <Badge key={i} variant="outline" className="text-[9px]">
              {t.language}
              {t.framework ? `/${t.framework}` : ''}
            </Badge>
          ))}
        </div>
      ) : (
        <div className="rounded bg-surface-elevated/50 p-2 text-center text-[10px] text-text-tertiary">
          No tech stack detected
        </div>
      )}
    </div>
  );
}
